// ADR 33 — host side of the MCP Apps bridge (SEP-1865), using the official SDK
// `@modelcontextprotocol/ext-apps/app-bridge`. We pass `null` for the MCP client
// (our web is a thin REST host, not a full MCP client) and register our own
// `tools/call` handler that forwards to the gateway proxy (`/api/ui/tools/call`),
// which enforces tenant + audit + entitlement + consent. The bridge handles the
// `ui/initialize` handshake; on completion we push the app's render data.
//
// Attaching is non-breaking: if the iframe content doesn't speak the protocol
// (e.g. a plain rawHtml render), the bridge simply never initializes and the
// static render still shows.

import { AppBridge, PostMessageTransport } from '@modelcontextprotocol/ext-apps/app-bridge';
import { CallToolRequestSchema } from '@modelcontextprotocol/sdk/types.js';
import { uiToolCall } from '../api/client';

export interface DockAppBridge {
  close(): void;
}

interface AttachOpts {
  /** Render data pushed to the app once the handshake completes (optional — our
   *  rawHtml is self-contained, so this is mostly for data-driven app templates). */
  appData?: unknown;
  /** Called when the app reports a content size change, to resize the frame. */
  onSizeChange?: (height: number) => void;
  /** Ask the dock to confirm a state-mutating tool call. Resolve true to proceed. */
  confirm?: (toolName: string) => Promise<boolean>;
}

type CallToolParams = { name: string; arguments?: Record<string, unknown> };

export function attachAppBridge(iframe: HTMLIFrameElement, opts: AttachOpts = {}): DockAppBridge {
  const win = iframe.contentWindow;
  if (!win) return { close: () => {} };

  const bridge = new AppBridge(
    null,
    { name: 'lymon-dock', version: '1.0.0' },
    // We proxy tool calls + resource reads and can open links on the app's behalf.
    { serverTools: {}, serverResources: {}, openLinks: {} },
  );

  // App → host `tools/call` → consented gateway proxy. A mutating tool comes back
  // as `needs_confirmation`; ask the dock and retry once confirmed.
  bridge.setRequestHandler(CallToolRequestSchema, async (req: { params: CallToolParams }) => {
    const { name, arguments: args = {} } = req.params;
    let r = await uiToolCall(name, args);
    if (r.needs_confirmation) {
      const ok = opts.confirm ? await opts.confirm(name) : false;
      if (!ok) return { content: [{ type: 'text', text: 'cancelled by user' }], isError: true };
      r = await uiToolCall(name, args, true);
    }
    return {
      content: [{ type: 'text', text: JSON.stringify(r.error ? r : (r.result ?? r)) }],
      isError: Boolean(r.error),
    };
  });

  if (opts.onSizeChange) {
    bridge.onsizechange = ({ height }: { height?: number | null }) => {
      if (typeof height === 'number') opts.onSizeChange!(height);
    };
  }
  bridge.oninitialized = () => {
    if (opts.appData !== undefined) {
      // Param shape is the spec's tool-result notification; cast since we feed
      // an opaque app payload, not a typed CallToolResult.
      void bridge.sendToolResult(opts.appData as never).catch(() => {});
    }
  };

  // Host transport: post into the iframe, listen on our window.
  void bridge.connect(new PostMessageTransport(win, window)).catch(() => {});

  return { close: () => { void (bridge as { close?: () => void }).close?.(); } };
}
